import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { Heading, Flex, Box, Button, Progress, Text, useToast, IconButton, Tooltip } from '@chakra-ui/react';
import { IoCheckmarkCircle, IoCheckmarkCircleOutline } from 'react-icons/io5';
import { fetchData, fetchSurveyData } from '../../../services/fetch';
import baseService from '../../../services/base';
import { useSSO } from '../../../context/SSO';
import { QuestionCardSimple } from '../../../components/Card';
import { downloadFile } from '../../../helpers/downloadFile';
import { DownloadResponsesButton, SubmitAnswersButton } from '../../../components/Button';
import RespondentView from '../../../components/Survey/RespondentView';
import { initializeDefaultValues } from './defaultValues';
import StatusTag from '../../../components/Tags/StatusTag';
import { EditStatusModal, EvidenceViewerModal } from '../../../components/Modals';
import { getStatusMessage } from '../../../helpers/certificationStatus';
import CertificationStatusPanel from '../../../components/Certification/CertificationStatusPanel';

export default function BasicCertification({
 selectedCertificationId,
 selectedCompanyId,
 companyGroupId,
 isResponsibleUser = false,
 idLinkGroup,
 linkStatus,
}) {
 const toast = useToast();
 const { user, hasMasterRole } = useSSO();
 const isMaster = hasMasterRole();

 const [isEvidenceModalOpen, setEvidenceModalOpen] = useState(false);
 const [currentQuestionId, setCurrentQuestionId] = useState();
 const [currentFiles, setCurrentFiles] = useState([]);
 const [isLoadingEvidence, setLoadingEvidence] = useState(false);
 const [isLoadingResponses, setLoadingResponses] = useState(false);
 const [isViewMode, setIsViewMode] = useState(false);
 const [certificationStatus, setCertificationStatus] = useState(null);
 const [isEditStatusModalOpen, setIsEditStatusModalOpen] = useState(false);
 const [certLinkStatus, setCertLinkStatus] = useState(linkStatus);
 const [isTogglingLinkStatus, setIsTogglingLinkStatus] = useState(false);

 useEffect(() => {
  setCertLinkStatus(linkStatus);
 }, [linkStatus]);

 const { data: questionnaireData, isLoading } = useSWR(
  selectedCertificationId
   ? `certificate/questionary/${selectedCertificationId}?id_company=${selectedCompanyId}`
   : null,
  fetchSurveyData
 );

 const {
  data: statusData,
  isLoading: isLoadingStatus,
  mutate,
 } = useSWR('certificate-status-control', fetchData);

 useMemo(() => {
  if (statusData && selectedCertificationId && selectedCompanyId) {
   const matchingStatus = statusData.find(
    (status) =>
     status.certificate.id === selectedCertificationId &&
     status.company.id_company === parseInt(selectedCompanyId)
   );
   if (matchingStatus) {
    setCertificationStatus(matchingStatus);
   }
  }
 }, [statusData, selectedCertificationId, selectedCompanyId]);

 const questions = questionnaireData?.questions;
 const formName = questionnaireData?.description;

 const toggleLinkStatus = async () => {
  if (!isMaster || !idLinkGroup) return;
  const newStatus = !certLinkStatus;
  setIsTogglingLinkStatus(true);
  try {
   await baseService.patch(`/certificate/link-group/${idLinkGroup}/status`, { status: newStatus });
   setCertLinkStatus(newStatus);
   toast({ title: newStatus ? 'Certificação ativada' : 'Certificação desativada', status: 'success', duration: 3000, isClosable: true });
  } catch {
   toast({ title: 'Erro ao alterar status', status: 'error', duration: 3000, isClosable: true });
  } finally {
   setIsTogglingLinkStatus(false);
  }
 };

 const currentQuestions = useMemo(() => {
  return (
   questions?.filter(
    (question) => question?.company?.id_company === +selectedCompanyId
   ) || []
  );
 }, [questions, selectedCompanyId]);

 const defaultValues = useMemo(() => {
  return currentQuestions.length ? initializeDefaultValues(currentQuestions) : {};
 }, [currentQuestions]);

 const totalQuestions = currentQuestions?.length || 0;

 const answeredQuestionsCount = useMemo(() => {
  return currentQuestions.filter((question) => {
   // Verificar se tem resposta
   const hasResponse =
    question.user_responses ||
    (Array.isArray(question.users_responses) &&
     question.users_responses.some(
      (response) => response?.company?.id_company === +selectedCompanyId
     )) ||
    question.users_responses;

   if (!hasResponse) return false;

   // Se a questão tem revisor vinculado, verificar aprovação
   if (question.has_reviewer) {
    // Para user_responses (resposta única)
    if (question.user_responses) {
     return question.user_responses.reviewer_approved === true;
    }

    // Para users_responses (múltiplas respostas)
    if (Array.isArray(question.users_responses)) {
     return question.users_responses.some(
      (response) =>
       response?.company?.id_company === +selectedCompanyId &&
       response.reviewer_approved === true
     );
    }

    // Para users_responses (objeto único)
    if (question.users_responses) {
     return question.users_responses.reviewer_approved === true;
    }

    return false;
   }

   // Se não tem revisor, apenas verificar se tem resposta
   return true;
  }).length;
 }, [currentQuestions, selectedCompanyId]);

 const allRequiredQuestionsAnswered = useMemo(() => {
  if (!currentQuestions?.length) return false;

  const requiredQuestions = currentQuestions.filter((question) => question.is_required);

  return requiredQuestions.every((question) => {
   const response = question.user_responses || question.users_responses;
   return (
    response?.response || (response?.response_file && response.response_file.length > 0)
   );
  });
 }, [currentQuestions]);

 const { requiredQuestionsCount, answeredRequiredQuestionsCount } = useMemo(() => {
  if (!currentQuestions?.length) {
   return { requiredQuestionsCount: 0, answeredRequiredQuestionsCount: 0 };
  }

  const requiredQuestions = currentQuestions.filter((question) => question.is_required);
  const requiredQuestionsCount = requiredQuestions.length;

  const answeredRequiredQuestionsCount = requiredQuestions.filter((question) => {
   // Verificar se tem resposta
   const hasResponse =
    question.user_responses ||
    (Array.isArray(question.users_responses) &&
     question.users_responses.some(
      (response) => response?.company?.id_company === +selectedCompanyId
     )) ||
    question.users_responses;

   if (!hasResponse) return false;

   // Se a questão tem revisor vinculado, verificar aprovação
   if (question.has_reviewer) {
    // Para user_responses (resposta única)
    if (question.user_responses) {
     return question.user_responses.reviewer_approved === true;
    }

    // Para users_responses (múltiplas respostas)
    if (Array.isArray(question.users_responses)) {
     return question.users_responses.some(
      (response) =>
       response?.company?.id_company === +selectedCompanyId &&
       response.reviewer_approved === true
     );
    }

    // Para users_responses (objeto único)
    if (question.users_responses) {
     return question.users_responses.reviewer_approved === true;
    }

    return false;
   }

   // Se não tem revisor, apenas verificar se tem resposta
   return true;
  }).length;

  return { requiredQuestionsCount, answeredRequiredQuestionsCount };
 }, [currentQuestions, selectedCompanyId]);

 const hasEvidenceFiles = useMemo(() => {
  return currentQuestions?.some((question) => {
   const response = question.user_responses || question.users_responses;
   return response?.response_file?.length > 0;
  });
 }, [currentQuestions, selectedCompanyId]);

 const handleQuestionEvidence = useCallback((questionId, response) => {
  setCurrentQuestionId(questionId);
  setCurrentFiles(response?.response_file);
  setEvidenceModalOpen(true);
 }, []);

 const handleCloseCurrentEvidence = () => {
  setEvidenceModalOpen(false);
  setCurrentQuestionId(undefined);
  setCurrentFiles(undefined);
 };

 async function evidenceDownload() {
  setLoadingEvidence(true);
  try {
   const response = await baseService.get(
    `/downloads?survey=${questionnaireData?.id_survey}&group=${companyGroupId}&form=${formName}`,
    { responseType: 'arraybuffer' }
   );

   if (response.headers['content-type'].includes('application/json')) {
    const data = JSON.parse(new TextDecoder().decode(response.data));
    downloadFile(data.url, `${formName}_evidence.zip`);
   } else {
    const blob = new Blob([response.data], { type: 'application/zip' });
    downloadFile(window.URL.createObjectURL(blob), `${formName}_evidence.zip`);
   }
   toast({
    title: 'Download das evidências iniciado com sucesso',
    status: 'success',
   });
  } catch (error) {
   toast({
    title: 'Erro ao fazer o download das evidências',
    description: 'Não foi possível processar os arquivos anexados. Tente novamente.',
    status: 'error',
   });
  } finally {
   setLoadingEvidence(false);
  }
 }

 if (isLoading || isLoadingStatus) {
  return null;
 }

 if (!currentQuestions?.length) {
  return (
   <Box mt='6' bg='white' p='6'>
    <Text>Nenhuma questão respondida até o momento</Text>
   </Box>
  );
 }

 if (isViewMode) {
  return (
   <RespondentView
    defaultValues={defaultValues}
    surveyId={questionnaireData?.id_survey}
    companyId={selectedCompanyId}
    userId={user?.user?.id_user}
    questions={currentQuestions}
    companyName={currentQuestions[0]?.company?.name}
    description={formName}
    totalAnsweredQuestions={answeredQuestionsCount}
    requiredQuestionsCount={requiredQuestionsCount}
    answeredRequiredQuestionsCount={answeredRequiredQuestionsCount}
    onClose={() => setIsViewMode(false)}
   />
  );
 }

 return (
  <Box>
   {certificationStatus?.id && (
    <CertificationStatusPanel control={certificationStatus} />
   )}

   {certificationStatus?.status &&
    ['em auditoria', 'em conferência final', 'em ajustes', 'em avaliação', 'em revisão', 'aguardando adequações'].includes(
     certificationStatus.status
    ) && (
     <Flex
      align='center'
      justify='center'
      w='full'
      h='46px'
      my='24px'
      bg='#FBDED5'
      borderRadius='17px'
     >
      <Text color='#EA582B' fontWeight={500}>
       {getStatusMessage(certificationStatus.status, certificationStatus.updatedAt)}
      </Text>
     </Flex>
    )}

   <Box bg='white' p='16px' borderRadius='12px'>
    <Flex h='63px' px='16px' bg='#F9F9F9' align='center' gap='12px' mb='16px'>
     <Flex gap='12px' h='31px' align='center' ml='auto'>
      <Text fontWeight={700}>Status:</Text>
      <StatusTag status={certificationStatus?.status || 'não enviado'} />
     </Flex>

     {!isResponsibleUser &&
      certificationStatus?.status &&
      certificationStatus?.status !== 'não enviado' && (
       <Button
        variant='underline'
        fontWeight={600}
        fontSize={'15px'}
        onClick={() => setIsEditStatusModalOpen(true)}
       >
        Editar status
       </Button>
      )}
    </Flex>
    <Flex h='51px' align='center' justify='space-between' gap='6'>
     <Heading as='h3' fontSize='md'>
      Questões
     </Heading>

     {!isResponsibleUser && (
      <Button
       variant='underline'
       fontWeight={600}
       ml='auto'
       onClick={() => setIsViewMode(true)}
      >
       Visualizar como respondente
      </Button>
     )}

     <Flex align='center' gap='6'>
      {!isResponsibleUser && (
       <Flex
        h='51px'
        align='center'
        gap='14px'
        px='20px'
        bg='rgba(0, 115, 187, 0.1)'
        borderRadius='12px'
       >
        <Text as='span' fontSize='sm'>
         Respondidas
        </Text>

        <Box height='10px' w='114px'>
         <Progress
          value={(answeredQuestionsCount / totalQuestions) * 100}
          height='10px'
          borderRadius='21px'
          bg='#E5E5E5'
         />
        </Box>
        <Text
         as='span'
         fontSize='sm'
         bg='white'
         fontWeight={600}
         p='8px 10px'
         h='35px'
         borderRadius='4px'
        >
         {answeredQuestionsCount < 10 && '0'}
         {answeredQuestionsCount}/{totalQuestions < 10 && '0'}
         {totalQuestions}
        </Text>
       </Flex>
      )}

      <Flex
       h='51px'
       align='center'
       gap='14px'
       px='20px'
       bg='rgba(234, 88, 43, 0.1)'
       borderRadius='12px'
      >
       <Text as='span' fontSize='sm'>
        Obrigatórias
       </Text>

       <Box height='10px' w='114px'>
        <Progress
         value={(answeredRequiredQuestionsCount / requiredQuestionsCount) * 100}
         height='10px'
         borderRadius='21px'
         bg='#E5E5E5'
         colorScheme={isResponsibleUser ? 'green' : 'orange'}
        />
       </Box>
       <Text
        as='span'
        fontSize='sm'
        bg='white'
        fontWeight={600}
        p='8px 10px'
        h='35px'
        borderRadius='4px'
       >
        {answeredRequiredQuestionsCount < 10 && '0'}
        {answeredRequiredQuestionsCount}/{requiredQuestionsCount < 10 && '0'}
        {requiredQuestionsCount}
       </Text>
      </Flex>
     </Flex>

     {isMaster && (
       <Tooltip label={certLinkStatus ? 'Desativar certificação' : 'Ativar certificação'}>
        <IconButton
         icon={certLinkStatus ? <IoCheckmarkCircle size={22} color='#38A169' /> : <IoCheckmarkCircleOutline size={22} color='#A0AEC0' />}
         variant='ghost'
         size='sm'
         isLoading={isTogglingLinkStatus}
         onClick={toggleLinkStatus}
         aria-label='Alternar status'
        />
       </Tooltip>
      )}

     <Flex gap='5' overflowY='hidden' pos='absolute' right='32px' top='70px' align='center'>
      <DownloadResponsesButton
       surveyId={questionnaireData?.id_survey}
       companyId={selectedCompanyId}
       companyGroupId={companyGroupId}
       formName={formName}
       isLoadingResponses={isLoadingResponses}
       setLoadingResponses={setLoadingResponses}
      />

      <Button
       variant='secondary'
       width='260px'
       height='51px'
       isLoading={isLoadingEvidence}
       isDisabled={!hasEvidenceFiles}
       onClick={evidenceDownload}
      >
       Download das evidências
      </Button>

      {isResponsibleUser && (certificationStatus?.status || !certificationStatus) && (
       <SubmitAnswersButton
        certificationStatusId={certificationStatus?.id}
        mutate={mutate}
        isEnabled={
         (!certificationStatus || certificationStatus?.status === 'não enviado') &&
         allRequiredQuestionsAnswered
        }
        selectedCertification={selectedCertificationId}
        companyId={selectedCompanyId}
        idCompanyGroup={companyGroupId}
        userId={user?.user?.id_user}
       />
      )}
     </Flex>
    </Flex>
   </Box>

   <Box mt='6'>
    {currentQuestions.map((question, index) => (
     <QuestionCardSimple
      key={`${question?.id_question}_${index}`}
      question={question}
      selectedCompanyId={selectedCompanyId}
      handleQuestionEvidence={handleQuestionEvidence}
      surveyId={questionnaireData?.id_survey}
      userId={user?.user?.id_user}
      questionnaireType='certification'
      isResponsibleUser={isResponsibleUser}
     />
    ))}
   </Box>

   <EditStatusModal
    isOpen={isEditStatusModalOpen}
    onClose={() => setIsEditStatusModalOpen(false)}
    currentStatus={certificationStatus?.status}
    certificateId={certificationStatus?.id}
    onStatusUpdate={() => mutate()}
    certificateStatusControlId={certificationStatus?.id}
    control={certificationStatus}
   />

   {currentQuestionId && (
    <EvidenceViewerModal
     isOpen={isEvidenceModalOpen}
     onClose={handleCloseCurrentEvidence}
     files={currentFiles}
     isAdminView
    />
   )}
  </Box>
 );
}
